import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';

/**
 * Hook for managing influencer profile data with real-time updates
 * Provides profile data, owner status, and update functions
 * Uses Firestore real-time listener so changes are reflected immediately
 */
export function useInfluencerProfile(username) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  const resolvedUsername = typeof username === 'string' ? username.trim() : '';
  const isValidUsername = resolvedUsername && resolvedUsername !== 'u';

  // Step 1: Find userId by username (one-time lookup)
  useEffect(() => {
    if (!isValidUsername) {
      if (username !== undefined && username !== null && String(username).trim() !== '') {
        console.warn('[useInfluencerProfile] Invalid username (skip query):', { received: username, type: typeof username });
      }
      setLoading(false);
      setProfile(null);
      setError(resolvedUsername === 'u' ? 'Invalid profile URL' : null);
      setUserId(null);
      return;
    }

    let isMounted = true;
    const trimmedUsername = resolvedUsername;
    console.log('[useInfluencerProfile] passed username (debug):', trimmedUsername);

    const findUserId = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Query by username to get userId
        const { getInfluencerByUsername } = await import('../lib/models/influencer');
        const influencer = await getInfluencerByUsername(trimmedUsername);
        
        if (!isMounted) return;
        
        if (influencer && influencer.userId) {
          console.log('[useInfluencerProfile] Found userId:', influencer.userId, 'for username:', trimmedUsername);
          console.log('[useInfluencerProfile] Initial profile data from username lookup:', JSON.parse(JSON.stringify(influencer)));
          console.log('[useInfluencerProfile] Initial profile keys:', Object.keys(influencer));
          console.log('[useInfluencerProfile] Initial data check:', {
            hasBio: !!influencer.bio,
            hasPlatformStats: !!influencer.platformStats && Object.keys(influencer.platformStats || {}).length > 0,
            hasContactEmail: !!influencer.contactEmail,
            hasNiche: !!influencer.niche,
            hasPhotoURL: !!influencer.photoURL,
            hasProfileImageUrl: !!influencer.profileImageUrl,
            hasAvatarUrl: !!influencer.avatarUrl,
            platformStatsKeys: influencer.platformStats ? Object.keys(influencer.platformStats) : [],
          });
          setUserId(influencer.userId);
          // CRITICAL: Set profile immediately from username lookup (original working behavior)
          // The real-time listener will update it if it fires, but this ensures we have data
          console.log('[useInfluencerProfile] ✅ Setting profile from username lookup');
          setProfile(influencer);
          setLoading(false);
          // Real-time listener will update if it fires, but we don't wait for it
        } else {
          console.warn('[useInfluencerProfile] Profile not found for username:', trimmedUsername);
          setError('Profile not found');
          setProfile(null);
          setUserId(null);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useInfluencerProfile] Error finding userId:', err);
        if (!isMounted) return;
        setError(err.message || 'Failed to load profile');
        setProfile(null);
        setUserId(null);
        setLoading(false);
      }
    };

    findUserId();

    return () => {
      isMounted = false;
    };
  }, [resolvedUsername]);

  // Step 2: Set up real-time listener once we have userId
  useEffect(() => {
    if (!userId) {
      return;
    }

    console.log('[useInfluencerProfile] 🔥 Setting up real-time listener for userId:', userId);
    const influencerRef = doc(db, 'influencers', userId);
    console.log('[useInfluencerProfile] 🔥 Document path: influencers/' + userId);
    
    // CRITICAL: Do a direct read FIRST to verify what's in Firestore
    // This helps us debug if the listener is working correctly
    getDoc(influencerRef).then((docSnapshot) => {
      if (docSnapshot.exists()) {
        const directReadData = docSnapshot.data();
        console.log('[useInfluencerProfile] ========== DIRECT FIRESTORE READ ==========');
        console.log('[useInfluencerProfile] ✅ Document EXISTS at influencers/' + userId);
        console.log('[useInfluencerProfile] Direct read data:', JSON.parse(JSON.stringify(directReadData)));
        console.log('[useInfluencerProfile] Direct read keys:', Object.keys(directReadData));
        console.log('[useInfluencerProfile] Direct read keys count:', Object.keys(directReadData).length);
        console.log('[useInfluencerProfile] Direct read check:', {
          hasBio: !!directReadData.bio,
          bio: directReadData.bio,
          hasPlatformStats: !!directReadData.platformStats && Object.keys(directReadData.platformStats || {}).length > 0,
          platformStats: directReadData.platformStats,
          platformStatsKeys: directReadData.platformStats ? Object.keys(directReadData.platformStats) : [],
          hasContactEmail: !!directReadData.contactEmail,
          contactEmail: directReadData.contactEmail,
          hasPhotoURL: !!directReadData.photoURL,
          photoURL: directReadData.photoURL,
          hasProfileImageUrl: !!directReadData.profileImageUrl,
          profileImageUrl: directReadData.profileImageUrl,
          hasAvatarUrl: !!directReadData.avatarUrl,
          avatarUrl: directReadData.avatarUrl,
          hasLocation: !!directReadData.location,
          location: directReadData.location,
          hasNiche: !!directReadData.niche,
          niche: directReadData.niche,
          hasCreatorTitle: !!directReadData.creatorTitle,
          creatorTitle: directReadData.creatorTitle,
          hasSocialLinks: !!directReadData.socialLinks && Object.keys(directReadData.socialLinks || {}).length > 0,
        });
        console.log('[useInfluencerProfile] ===========================================');
        
        // CRITICAL: Always update from direct read - this is the FALLBACK if listener doesn't fire
        // Compare with current profile to see if we need to update
        const currentProfileKeys = profile ? Object.keys(profile).length : 0;
        const directReadKeys = Object.keys(directReadData).length;
        console.log('[useInfluencerProfile] Comparing: direct read has', directReadKeys, 'keys, current profile has', currentProfileKeys);
        
        if (directReadKeys > currentProfileKeys || !profile) {
          console.log('[useInfluencerProfile] 🔄 UPDATING profile from direct read (has more data)');
          const forceUpdatedProfile = {
            id: docSnapshot.id,
            userId: docSnapshot.id,
            ...directReadData,
            createdAt: directReadData.createdAt?.toMillis?.() || directReadData.createdAt || Date.now(),
            updatedAt: directReadData.updatedAt?.toMillis?.() || directReadData.updatedAt || Date.now(),
          };
          setProfile(forceUpdatedProfile);
          setLoading(false);
        } else {
          console.log('[useInfluencerProfile] ✅ Profile already has same or more data, skipping direct read update');
        }
      } else {
        console.warn('[useInfluencerProfile] ❌ Direct read: Document does NOT exist at influencers/' + userId);
      }
    }).catch((err) => {
      console.error('[useInfluencerProfile] ❌ Direct read error:', err);
    });

    // Set up real-time listener with error handling
    console.log('[useInfluencerProfile] 🔥 Setting up onSnapshot listener for:', influencerRef.path);
    
    // CRITICAL: Use onSnapshot with both success and error callbacks
    const unsubscribe = onSnapshot(
      influencerRef,
      (docSnapshot) => {
        console.log('[useInfluencerProfile] 🔔🔔🔔 onSnapshot callback FIRED!');
        console.log('[useInfluencerProfile] 🔔 Snapshot metadata:', {
          fromCache: docSnapshot.metadata.fromCache,
          hasPendingWrites: docSnapshot.metadata.hasPendingWrites,
        });
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          console.log('[useInfluencerProfile] 🔔 Raw data keys from listener:', Object.keys(data));
          console.log('[useInfluencerProfile] 🔔 Raw data keys count:', Object.keys(data).length);
          
          const updatedProfile = {
            id: docSnapshot.id,
            userId: docSnapshot.id,
            ...data,
            createdAt: data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
            updatedAt: data.updatedAt?.toMillis?.() || data.updatedAt || Date.now(),
          };
          
          console.log('[useInfluencerProfile] ========== REAL-TIME UPDATE RECEIVED ==========');
          console.log('[useInfluencerProfile] Raw Firestore document data:', JSON.parse(JSON.stringify(data)));
          console.log('[useInfluencerProfile] Document ID:', docSnapshot.id);
          console.log('[useInfluencerProfile] Document exists:', docSnapshot.exists());
          console.log('[useInfluencerProfile] Raw data keys:', Object.keys(data));
          console.log('[useInfluencerProfile] Raw data keys count:', Object.keys(data).length);
          console.log('[useInfluencerProfile] Updated profile object:', JSON.parse(JSON.stringify(updatedProfile)));
          console.log('[useInfluencerProfile] Profile keys:', Object.keys(updatedProfile));
          console.log('[useInfluencerProfile] Profile keys count:', Object.keys(updatedProfile).length);
          console.log('[useInfluencerProfile] Identity object:', JSON.parse(JSON.stringify(updatedProfile.identity || {})));
          console.log('[useInfluencerProfile] Platform stats:', JSON.parse(JSON.stringify(updatedProfile.platformStats || {})));
          console.log('[useInfluencerProfile] Social links:', JSON.parse(JSON.stringify(updatedProfile.socialLinks || {})));
          console.log('[useInfluencerProfile] Data completeness check:', {
            hasBio: !!updatedProfile.bio,
            bio: updatedProfile.bio,
            hasPlatformStats: !!updatedProfile.platformStats && Object.keys(updatedProfile.platformStats || {}).length > 0,
            platformStatsKeys: updatedProfile.platformStats ? Object.keys(updatedProfile.platformStats) : [],
            platformStatsValues: updatedProfile.platformStats,
            hasContactEmail: !!updatedProfile.contactEmail,
            contactEmail: updatedProfile.contactEmail,
            hasNiche: !!updatedProfile.niche,
            niche: updatedProfile.niche,
            hasPhotoURL: !!updatedProfile.photoURL,
            photoURL: updatedProfile.photoURL,
            hasProfileImageUrl: !!updatedProfile.profileImageUrl,
            profileImageUrl: updatedProfile.profileImageUrl,
            hasAvatarUrl: !!updatedProfile.avatarUrl,
            avatarUrl: updatedProfile.avatarUrl,
            hasLocation: !!updatedProfile.location,
            location: updatedProfile.location,
            hasCreatorTitle: !!updatedProfile.creatorTitle,
            creatorTitle: updatedProfile.creatorTitle,
            hasSocialLinks: !!updatedProfile.socialLinks && Object.keys(updatedProfile.socialLinks || {}).length > 0,
            socialLinksKeys: updatedProfile.socialLinks ? Object.keys(updatedProfile.socialLinks) : [],
          });
          console.log('[useInfluencerProfile] ===============================================');
          
          // CRITICAL: Always update profile state from real-time listener
          // This ensures we have the complete, up-to-date data from Firestore
          console.log('[useInfluencerProfile] ✅✅✅ UPDATING PROFILE STATE - keys:', Object.keys(updatedProfile).length);
          setProfile(updatedProfile);
          setLoading(false);
          setError(null);
        } else {
          console.warn('[useInfluencerProfile] ❌ Document does NOT exist for userId:', userId);
          setError('Profile not found');
          setProfile(null);
          setLoading(false);
        }
      },
      (err) => {
        console.error('[useInfluencerProfile] ❌❌❌ Real-time listener ERROR:', err);
        console.error('[useInfluencerProfile] Error code:', err.code);
        console.error('[useInfluencerProfile] Error message:', err.message);
        setError(err.message || 'Failed to load profile');
        setLoading(false);
      }
    );

    // CRITICAL: Verify listener was set up
    console.log('[useInfluencerProfile] ✅ Listener setup complete, unsubscribe function:', typeof unsubscribe);

    return () => {
      console.log('[useInfluencerProfile] 🧹 Cleaning up real-time listener');
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [userId]);

  // Calculate isOwner: only true if user is logged in AND matches profile owner
  // Explicitly check for null/undefined to handle logged-out users
  const isOwner = !!(user && profile && user.uid && profile.userId && user.uid === profile.userId);
  
  // Debug logging for owner detection
  useEffect(() => {
    if (profile) {
      console.log('[useInfluencerProfile] Owner check:', {
        hasUser: !!user,
        userUid: user?.uid,
        profileUserId: profile.userId,
        isOwner,
      });
    }
  }, [user, profile, isOwner]);

  /**
   * Update profile with partial data
   * Merges with existing data (does not overwrite)
   */
  const updateProfile = async (partialUpdate) => {
    if (!profile || !profile.userId) {
      throw new Error('Profile not loaded');
    }

    try {
      const influencerRef = doc(db, 'influencers', profile.userId);
      
      // Get existing document to merge properly
      const existingDoc = await getDoc(influencerRef);
      const existingData = existingDoc.exists() ? existingDoc.data() : {};
      
      // Remove undefined values and merge with existing data
      const cleanedUpdate = {};
      Object.keys(partialUpdate).forEach(key => {
        if (partialUpdate[key] !== undefined) {
          // Special handling for nested objects that need deep merging
          if (key === 'audience' && typeof partialUpdate[key] === 'object' && !Array.isArray(partialUpdate[key])) {
            console.log('Processing audience update:', {
              existing: existingData[key],
              incoming: partialUpdate[key]
            });
            // Merge audience data properly - deep merge nested objects
            const existingAudience = existingData[key] || {};
            const incomingAudience = partialUpdate[key] || {};
            
            // Start with existing audience data
            const mergedAudience = { ...existingAudience };
            
            // Deep merge each top-level field
            Object.keys(incomingAudience).forEach(audKey => {
              const incomingValue = incomingAudience[audKey];
              
              if (incomingValue === undefined || incomingValue === null) {
                // Skip undefined/null values
                return;
              }
              
              if (typeof incomingValue === 'object' && !Array.isArray(incomingValue)) {
                // Merge nested objects (gender, ageBrackets)
                mergedAudience[audKey] = {
                  ...(existingAudience[audKey] || {}),
                  ...incomingValue,
                };
                // Remove undefined/null from nested object
                const cleaned = {};
                Object.keys(mergedAudience[audKey]).forEach(nestedKey => {
                  const val = mergedAudience[audKey][nestedKey];
                  if (val !== undefined && val !== null) {
                    cleaned[nestedKey] = val;
                  }
                });
                mergedAudience[audKey] = cleaned;
              } else if (Array.isArray(incomingValue)) {
                // For arrays, replace entirely (topCountries, topCities)
                mergedAudience[audKey] = incomingValue.filter(item => 
                  item !== undefined && item !== null
                );
              } else {
                // Primitive values (primaryLanguage)
                mergedAudience[audKey] = incomingValue;
              }
            });
            
            console.log('Merged audience:', mergedAudience);
            
            // Clean the final merged audience - remove empty objects/arrays
            const cleanAudience = {};
            Object.keys(mergedAudience).forEach(audKey => {
              const audValue = mergedAudience[audKey];
              if (audValue !== undefined && audValue !== null) {
                if (typeof audValue === 'object' && !Array.isArray(audValue)) {
                  // Only include if it has at least one key
                  if (Object.keys(audValue).length > 0) {
                    cleanAudience[audKey] = audValue;
                  }
                } else if (Array.isArray(audValue)) {
                  // Only include if array has items
                  if (audValue.length > 0) {
                    cleanAudience[audKey] = audValue;
                  }
                } else {
                  // Include primitive values
                  cleanAudience[audKey] = audValue;
                }
              }
            });
            
            // Always save audience if it has any fields
            if (Object.keys(cleanAudience).length > 0) {
              cleanedUpdate[key] = cleanAudience;
              console.log('Cleaned audience data to save:', cleanAudience);
            } else {
              console.warn('Audience data became empty after cleaning, not saving');
            }
          } else if (key === 'platformStats' && typeof partialUpdate[key] === 'object' && !Array.isArray(partialUpdate[key])) {
            // Merge platformStats properly
            cleanedUpdate[key] = {
              ...(existingData[key] || {}),
              ...partialUpdate[key],
            };
          } else if (key === 'identity' && typeof partialUpdate[key] === 'object' && !Array.isArray(partialUpdate[key])) {
            // Merge identity properly
            cleanedUpdate[key] = {
              ...(existingData[key] || {}),
              ...partialUpdate[key],
            };
          } else if (key === 'reliability' && typeof partialUpdate[key] === 'object' && !Array.isArray(partialUpdate[key])) {
            // Merge reliability properly
            cleanedUpdate[key] = {
              ...(existingData[key] || {}),
              ...partialUpdate[key],
            };
          } else {
            cleanedUpdate[key] = partialUpdate[key];
          }
        }
      });
      
      console.log('Updating Firestore with:', cleanedUpdate);
      await updateDoc(influencerRef, {
        ...cleanedUpdate,
        updatedAt: serverTimestamp(),
      });
      console.log('Firestore update completed');
      
      // Verify the data was saved by reading it back
      const verifyDoc = await getDoc(influencerRef);
      if (verifyDoc.exists()) {
        const savedData = verifyDoc.data();
        console.log('Verified saved data in Firestore:', {
          audience: savedData.audience,
          hasAudience: !!savedData.audience,
          audienceKeys: savedData.audience ? Object.keys(savedData.audience) : []
        });
      }

      // Update local state by merging
      setProfile((prev) => {
        const updated = {
          ...prev,
          ...cleanedUpdate,
        };
        console.log('Updated local profile state, audience:', updated.audience);
        return updated;
      });

      return true;
    } catch (err) {
      console.error('Error updating profile:', err);
      throw err;
    }
  };

  // Convenience functions for specific sections
  // These merge with existing nested objects
  const updateIdentity = async (identityData) => {
    return updateProfile({
      identity: {
        ...(profile?.identity || {}),
        ...identityData,
      },
    });
  };

  const updatePlatformStats = async (platformStats) => {
    return updateProfile({
      platformStats: {
        ...(profile?.platformStats || {}),
        ...platformStats,
      },
    });
  };

  const updateAudience = async (audienceData) => {
    console.log('updateAudience called with:', audienceData);
    console.log('Current profile audience:', profile?.audience);
    // Pass the partial audience data directly - updateProfile will merge with Firestore data
    const result = await updateProfile({
      audience: audienceData,
    });
    console.log('updateAudience completed, result:', result);
    return result;
  };

  const updateBrandPartners = async (brandPartners) => {
    return updateProfile({ brandPartners });
  };

  const updateCollabHighlights = async (collabHighlights) => {
    return updateProfile({ collabHighlights });
  };

  const updateFeaturedPosts = async (featuredPosts) => {
    return updateProfile({ featuredPosts });
  };

  const updateReliability = async (reliabilityData) => {
    return updateProfile({
      reliability: {
        ...(profile?.reliability || {}),
        ...reliabilityData,
      },
    });
  };

  return {
    profile,
    loading,
    error,
    isOwner,
    updateProfile,
    updateIdentity,
    updatePlatformStats,
    updateAudience,
    updateBrandPartners,
    updateCollabHighlights,
    updateFeaturedPosts,
    updateReliability,
  };
}

